import { DEMO_TOKEN_DATA as D } from '../data/demo-intel-data.js';
import {
  ShieldCheckIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  LockIcon,
  SearchIcon,
  ActivityIcon,
} from '../components/Icons.js';
import { RISK_LEVEL_EXPLAIN, LEGAL_DISCLAIMER } from '../data/metric-explanations.js';
import { formatSafetyScore } from '../lib/scoreFormatter.js';

/* ------------------------------------------------------------------ */
/* Helpers (mirrored from TokenIntel)                                  */
/* ------------------------------------------------------------------ */

function truncAddr(addr: string): string {
  if (addr.length <= 12) return addr;
  return addr.slice(0, 6) + '...' + addr.slice(-4);
}

function fmtUsd(val: number | null | undefined): string {
  if (val == null) return 'No data available yet';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtPct(val: number | null | undefined): string {
  if (val == null) return 'No data available yet';
  return val.toFixed(1) + '%';
}

function fmtNum(val: number | null | undefined): string {
  if (val == null) return 'No data available yet';
  return val.toLocaleString('en-US');
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function ScoreCircle({ score, color, size = 120 }: { score: number; color: string; size?: number }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(score / 10, 1);
  const offset = circumference * (1 - pct);

  return (
    <div className="intel-score-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={'0 0 ' + size + ' ' + size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          transform={'rotate(-90 ' + size / 2 + ' ' + size / 2 + ')'}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <span className="intel-score-value" style={{ color }}>{score.toFixed(1)}</span>
    </div>
  );
}

function BoolBadge({ value, goodWhen }: { value: boolean; goodWhen: boolean }) {
  const isGood = value === goodWhen;
  return (
    <span className={'intel-bool-badge ' + (isGood ? 'intel-bool-good' : 'intel-bool-bad')}>
      {value ? 'Yes' : 'No'}
    </span>
  );
}

function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="intel-metric">
      <span className="intel-metric-label">{label}</span>
      <span className="intel-metric-value">{value}</span>
    </div>
  );
}

function DemoUnlockedBadge() {
  return (
    <span className="demo-unlocked-badge">DEMO</span>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

export function IntelDemo() {
  const riskColor = '#f39c12'; // YELLOW

  return (
    <section className="intel-page">
      <div className="container">

        {/* ======== DEMO BANNER ======== */}
        <div className="demo-banner">
          <div className="demo-banner-inner">
            <div className="demo-banner-icon-row">
              <SearchIcon className="demo-banner-icon" aria-hidden="true" />
              <span className="demo-banner-label">DEMO</span> <span className="ti-demo-label">DEMO DATA</span>
            </div>
            <h1 className="demo-banner-title">Pro-Tier Token Intelligence</h1>
            <p className="demo-banner-subtitle">
              This is sample data for demonstration purposes only. All values are fabricated.
            </p>
            <a className="btn btn-primary demo-banner-cta" href="/pricing">
              Upgrade for Live Analysis
            </a>
          </div>
        </div>

        {/* ======== TOKEN HEADER ======== */}
        <div className="intel-header">
          <div className="intel-header-left">
            <ScoreCircle score={formatSafetyScore(D.riskScore) ?? 0} color={riskColor} />
          </div>
          <div className="intel-header-right">
            <div className="intel-header-badges">
              <span className="intel-chain-badge" style={{ color: D.chainColor, borderColor: D.chainColor }}>
                {D.chainLabel}
              </span>
              <span className="intel-tier-badge" style={{ background: 'rgba(39,174,96,0.15)', color: '#27ae60', borderColor: '#27ae60' }}>
                Pro Intel
              </span>
              <DemoUnlockedBadge />
            </div>
            <h2 className="intel-token-name">
              {D.tokenName} <span className="intel-ticker">${D.tokenSymbol}</span>
            </h2>
            <div className="intel-token-addr">
              <code>{D.contractAddress}</code>
            </div>
            <p className="intel-headline">{D.headline}</p>
          </div>
        </div>

        {/* ======== RISK BANNER ======== */}
        <div className="intel-risk-banner" style={{ borderColor: riskColor, background: 'rgba(243,156,18,0.06)' }}>
          <AlertTriangleIcon className="intel-risk-banner-icon" style={{ color: riskColor }} aria-hidden="true" />
          <div>
            <strong style={{ color: riskColor }}>{D.riskLabel}</strong>
            <span className="intel-risk-banner-score"> &mdash; Risk Assessment {(formatSafetyScore(D.riskScore) ?? 0).toFixed(1)} / 10</span>
          </div>
          <span className="ti-risk-summary ti-risk-summary-yellow">
            {RISK_LEVEL_EXPLAIN.YELLOW.label}
          </span>
        </div>

        {/* ======== ANALYSIS SUMMARY ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <ShieldCheckIcon className="intel-section-icon" aria-hidden="true" />
            Analysis Summary
            <DemoUnlockedBadge />
          </h2>
          <div className="card intel-summary-card">
            <p>{D.summary}</p>
          </div>
        </div>

        {/* ======== SELLABILITY / CONTRACT ANALYSIS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <LockIcon className="intel-section-icon" aria-hidden="true" />
            Sellability Check
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-3">
            <div className="intel-metric-card card">
              <MetricRow label="Sellability Score" value={
                <span style={{ color: '#27ae60' }}>{formatSafetyScore(D.contractSafety.score, 100)?.toFixed(1) ?? '0.0'} / 10</span>
              } />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Buy Tax" value={fmtPct(D.contractSafety.buyTaxPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Sell Tax" value={fmtPct(D.contractSafety.sellTaxPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Max TX" value={fmtPct(D.contractSafety.maxTxPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Max Wallet" value={fmtPct(D.contractSafety.maxWalletPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Ownership Renounced" value={<BoolBadge value={D.contractSafety.ownershipRenounced} goodWhen={true} />} />
            </div>
          </div>
          <div className="intel-grid intel-grid-3" style={{ marginTop: '0.75rem' }}>
            <div className="intel-metric-card card">
              <MetricRow label="Can Create New Tokens" value={<BoolBadge value={D.contractSafety.hasMintFunction} goodWhen={false} />} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Can Block Wallets" value={<BoolBadge value={D.contractSafety.hasBlacklist} goodWhen={false} />} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Can Pause Trading" value={<BoolBadge value={D.contractSafety.hasPauseFunction} goodWhen={false} />} />
            </div>
          </div>
        </div>

        {/* ======== LIQUIDITY ANALYSIS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <ActivityIcon className="intel-section-icon" aria-hidden="true" />
            Liquidity Analysis
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-3">
            <div className="intel-metric-card card">
              <MetricRow label="Total Liquidity" value={fmtUsd(D.liquidity.totalLiquidityUsd)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="LP Locked" value={<BoolBadge value={D.liquidity.lpLocked} goodWhen={true} />} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Lock Duration" value={D.liquidity.lpLockMonths + ' months'} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Lock Platform" value={D.liquidity.lpLockPlatform} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Pool Count" value={D.liquidity.poolCount} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Depth Score" value={
                <span style={{ color: '#27ae60' }}>{D.liquidity.depthScore}/10</span>
              } />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Initial Liquidity" value={fmtUsd(D.liquidity.initialLiquidityUsd)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Current Liquidity" value={fmtUsd(D.liquidity.currentLiquidityUsd)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="24h Change" value={
                <span style={{ color: '#27ae60' }}>+{fmtPct(D.liquidity.liquidityChange24h)}</span>
              } />
            </div>
          </div>
        </div>

        {/* ======== HOLDER DISTRIBUTION ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            Holder Distribution
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-4">
            <div className="intel-metric-card card">
              <MetricRow label="Total Holders" value={fmtNum(D.holders.totalHolders)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Top 10 Hold" value={fmtPct(D.holders.top10HoldersPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Whale Count" value={fmtNum(D.holders.whaleCount)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Concentration" value={
                <span style={{ color: '#27ae60' }}>{D.holders.concentration}</span>
              } />
            </div>
          </div>
          <div className="intel-grid intel-grid-4" style={{ marginTop: '0.75rem' }}>
            <div className="intel-metric-card card">
              <MetricRow label="Top 20 Holders" value={fmtPct(D.holders.top20HoldersPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Dev Holds" value={fmtPct(D.holders.devHoldsPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Burned" value={fmtPct(D.holders.burnedPct)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Signal" value={
                <span style={{ color: '#27ae60', fontWeight: 700 }}>{D.smartMoney.signal}</span>
              } />
            </div>
          </div>
        </div>

        {/* ======== SMART MONEY SIGNALS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            Smart Money Signals
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-4">
            <div className="intel-metric-card card">
              <MetricRow label="Smart Buys (24h)" value={fmtNum(D.smartMoney.smartBuys24h)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Smart Sells (24h)" value={fmtNum(D.smartMoney.smartSells24h)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Net Flow (24h)" value={
                <span style={{ color: '#27ae60' }}>+{fmtUsd(D.smartMoney.netFlow24h)}</span>
              } />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Cluster Conviction" value={
                <span style={{ color: '#27ae60' }}>{D.smartMoney.clusterConviction.toFixed(1)}/10</span>
              } />
            </div>
          </div>
          {D.smartMoney.holders.length > 0 && (
            <div className="card intel-smart-money-table" style={{ marginTop: '0.75rem' }}>
              <table>
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Type</th>
                    <th>Label</th>
                    <th>Conviction</th>
                  </tr>
                </thead>
                <tbody>
                  {D.smartMoney.holders.map((h, i) => (
                    <tr key={i}>
                      <td><code>{truncAddr(h.address)}</code></td>
                      <td>{h.type}</td>
                      <td>{h.label}</td>
                      <td>{h.conviction.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ======== NETWORK ANALYSIS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            Network Analysis
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-4" style={{ marginBottom: '0.75rem' }}>
            <div className="intel-metric-card card">
              <MetricRow label="Connected Entities" value={fmtNum(D.network.connectedEntities)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Suspicious Links" value={
                <span style={{ color: D.network.suspiciousLinks > 0 ? '#f39c12' : '#27ae60' }}>
                  {D.network.suspiciousLinks}
                </span>
              } />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Exchange Flows" value={D.network.exchangeFlows} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="DEX Activity" value={
                <span style={{ color: '#27ae60' }}>{D.network.dexActivity}</span>
              } />
            </div>
          </div>
          <div className="intel-grid intel-grid-2">
            <div className="card">
              <h3 className="intel-subsection-title">
                Upstream Funders ({D.network.upstreamFunders.length})
              </h3>
              <ul className="intel-address-list">
                {D.network.upstreamFunders.map((f, i) => (
                  <li key={i}>
                    <code>{truncAddr(f.address)}</code>
                    {f.label && <span className="intel-address-label">{f.label}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card">
              <h3 className="intel-subsection-title">
                Downstream Distributors ({D.network.downstreamDistributors.length})
              </h3>
              <ul className="intel-address-list">
                {D.network.downstreamDistributors.map((d, i) => (
                  <li key={i}>
                    <code>{truncAddr(d.address)}</code>
                    {d.label && <span className="intel-address-label">{d.label}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ======== EXPLOIT & CONTRACT FLAGS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <ShieldCheckIcon className="intel-section-icon" aria-hidden="true" />
            Exploit &amp; Contract Flags
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-4">
            <div className="intel-metric-card card">
              <MetricRow label="Source Verified" value={<BoolBadge value={D.contractFlags.sourceVerified} goodWhen={true} />} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Proxy Contract" value={<BoolBadge value={D.contractFlags.proxyContract} goodWhen={false} />} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Known Exploits" value={
                <span style={{ color: '#27ae60' }}>{D.contractFlags.knownExploits}</span>
              } />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Audit Status" value={
                <span style={{ color: '#f39c12' }}>{D.contractFlags.auditStatus}</span>
              } />
            </div>
          </div>
        </div>

        {/* ======== SOCIAL METRICS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            Social Metrics
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-grid intel-grid-4">
            <div className="intel-metric-card card">
              <MetricRow label="X Mentions/hr" value={fmtNum(D.social.xMentionsPerHour)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="X Followers" value={fmtNum(D.social.xFollowers)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Telegram" value={fmtNum(D.social.telegramMembers)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Discord" value={fmtNum(D.social.discordMembers)} />
            </div>
          </div>
          <div className="intel-grid intel-grid-4" style={{ marginTop: '0.75rem' }}>
            <div className="intel-metric-card card">
              <MetricRow label="X Engagement" value={fmtPct(D.social.xEngagementRate)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="TG Growth Rate" value={fmtPct(D.social.telegramGrowthRate)} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Website Age" value={D.social.websiteAgeDays + ' days'} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="GitHub (30d)" value={fmtNum(D.social.githubCommits30d)} />
            </div>
          </div>
        </div>

        {/* ======== ACTIVE SIGNALS ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            Active Signals
            <DemoUnlockedBadge />
          </h2>
          <div className="intel-signals-list">
            {D.signals.map((sig, i) => (
              <div key={i} className={'intel-signal intel-signal-' + sig.severity}>
                <span className="intel-signal-badge">{sig.severity}</span>
                <span className="intel-signal-type">{sig.type}</span>
                <span className="intel-signal-msg">{sig.message}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ======== RISKS & POSITIVES ======== */}
        <div className="intel-section">
          <div className="intel-grid intel-grid-2">
            <div className="card intel-list-card intel-risks-card">
              <h3 className="intel-subsection-title" style={{ color: '#e74c3c' }}>
                <AlertTriangleIcon className="intel-section-icon" style={{ color: '#e74c3c' }} aria-hidden="true" />
                Risks ({D.risks.length})
              </h3>
              <ul>
                {D.risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
            <div className="card intel-list-card intel-positives-card">
              <h3 className="intel-subsection-title" style={{ color: '#27ae60' }}>
                <CheckCircleIcon className="intel-section-icon" style={{ color: '#27ae60' }} aria-hidden="true" />
                Positives ({D.positives.length})
              </h3>
              <ul>
                {D.positives.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* ======== EXPLAIN CHAIN ======== */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <SearchIcon className="intel-section-icon" aria-hidden="true" />
            Explain Chain (Confidence: {(D.confidence * 100).toFixed(0)}%)
          </h2>
          <div className="card demo-explain-chain">
            <ul>
              {D.explainChain.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* ======== BOTTOM UPGRADE CTA ======== */}
        <div className="demo-upgrade-cta card">
          <LockIcon className="demo-upgrade-cta-icon" aria-hidden="true" />
          <div className="demo-upgrade-cta-content">
            <h3>Pro Tier &mdash; $9.99/mo at Launch</h3>
            <p>Get live analysis for any token. Deep contract analysis, smart money tracking, network graph, and more.</p>
            <p className="demo-upgrade-cta-trust">Upgrade to unlock live analysis.</p>
          </div>
          <a href="/pricing" className="btn btn-primary btn-lg intel-locked-cta">
            View Pricing
          </a>
        </div>

        {/* ======== DEMO DISCLAIMER ======== */}
        <div className="demo-disclaimer">
          <p>
            <strong>Demo Data:</strong> This page displays fabricated sample data for demonstration purposes only.
            No real token analysis, API calls, or provider data are used. Actual Pro analysis results will vary.
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            {LEGAL_DISCLAIMER}
          </p>
        </div>

        {/* ======== META FOOTER ======== */}
        <footer className="intel-meta-footer">
          <span>Source: {D.meta.source} &middot; Version: {D.meta.analysisVersion}</span>
          <span>Demo page &mdash; no real data</span>
        </footer>

      </div>
    </section>
  );
}
